import React, { useState } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
  Box,
  Button,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
} from "@material-ui/core";
import { PrintOutlined } from "@material-ui/icons";
import fileDownload from "js-file-download";
import CircularCenteredLoader from "features/common/CircularCenteredLoader";
import validateDateAndFormat from "utils/validateDateAndFormat";
import DateRangePickerACCB from "./DateRangePickerACCB";
import { getBacCauseListReport, printBacCauseListReport } from "./BacReportApi";
import GlobalSearch from "../../Components/GlobalSearch/GlobalSearch";
import { displayEmail } from "Clients/ACCB/utils";

const useStyles = makeStyles(() => ({
  filtersContainer: {
    padding: "1rem",
    borderBottom: "1px solid #ddd",
    display: "flex",
    gap: "1rem",
    justifyContent: "flex-start",
    alignItems: "stretch",
    flexWrap: "wrap",
  },
  buttonSearch: {
    padding: "0.55rem 1.5rem",
    whiteSpace: "nowrap",
  },
  buttonPrint: {
    whiteSpace: "nowrap",
  },
  // Date header — light grey banner
  dateHeader: {
    fontWeight: 700,
    fontSize: "0.9375rem",
    padding: "10px 14px",
    backgroundColor: "#ede8e8",
    color: "black",
    letterSpacing: "0.02em",
  },
  // Sized up and given a full-width rule so
  // the start of each room section is findable when scrolling a long list.
  roomHeader: {
    fontWeight: 600,
    fontSize: "1rem",
    letterSpacing: "0.02em",
    padding: "12px 14px 12px 28px",
    backgroundColor: "#F5E6C8",
    color: "black",
    borderTop: "2px solid #61386d",
  },
  tableHeadRow: {
    backgroundColor: "#f5f5f5",
    "& th": {
      padding: "10px",
      fontWeight: 600,
      fontSize: "0.875rem",
    },
  },
  tableBodyCell: {
    padding: "10px",
    fontSize: "0.875rem",
    verticalAlign: "top",
  },
  tableWrapper: {
    maxWidth: "100%",
    padding: "0 12px",
    marginBottom: "1.5rem",
    overflowX: "auto",
  },
  counselLabel: {
    fontWeight: 600,
    fontSize: "0.8125rem",
    marginBottom: 4,
    color: TEXT.primary,
  },
  counselEntry: {
    fontSize: "0.8125rem",
    lineHeight: 1.6,
    color: TEXT.secondary,
  },
  counselGroup: {
    marginBottom: 10,
  },
  arbitratorLine: {
    fontSize: "0.875rem",
    lineHeight: 1.5,
    color: TEXT.primary,
  },
}));

const CounselGroup = ({ label, counsels, classes }) => {
  const counselList = Array.isArray(counsels) ? counsels : [];
  return (
    <Box className={classes.counselGroup}>
      <Typography className={classes.counselLabel}>{label}</Typography>
      {counselList.length === 0 ? (
        <Typography className={classes.counselEntry}>—</Typography>
      ) : (
        counselList.map((c, i) => (
          <Box key={i} style={{ marginBottom: 6 }}>
            <Typography className={classes.counselEntry}>{c.name || "—"}</Typography>
            {c.email && (
              <Typography className={classes.counselEntry}>{displayEmail(c.email)}</Typography>
            )}
            {c.phone_number && (
              <Typography className={classes.counselEntry}>{c.phone_number}</Typography>
            )}
          </Box>
        ))
      )}
    </Box>
  );
};

export default function BacCauseListReport() {
  const classes = useStyles();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [causeListType, setCauseListType] = useState("main");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // data shape: { "DD/MM/YYYY": { "Room Name": [cases] } }
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [dateError, setDateError] = useState("");
  const [search, setSearch] = useState("");

  const fetchCauseList = (start, end, searchText) => {
    setError("");
    setLoading(true);
    getBacCauseListReport(
      validateDateAndFormat(start, "yyyy-MM-dd"),
      validateDateAndFormat(end, "yyyy-MM-dd"),
      1,
      searchText,
      causeListType
    )
      .then((res) => {
        setData(res?.data?.data || {});
        setLoading(false);
      })
      .catch((err) => {
        console.error("Cause List Report fetch error:", err);
        setError("Failed to load report. Please try again.");
        setLoading(false);
      });
  };

  // Date range is mandatory — block Search / Download PDF until both dates are picked.
  const isDateRangeValid = () => {
    if (!startDate || !endDate) {
      setDateError("Please select both the From and To dates to continue.");
      return false;
    }
    if (new Date(endDate) < new Date(startDate)) {
      setDateError("To date must be equal to or greater than From date.");
      return false;
    }
    setDateError("");
    return true;
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setDateError("");
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setDateError("");
  };

  const handleSearch = () => {
    if (!isDateRangeValid()) return;
    fetchCauseList(startDate, endDate, search);
  };

  const handleSearchChange = (val) => {
    setSearch(val);
    if (startDate && endDate) {
      fetchCauseList(startDate, endDate, val);
    }
  };

  const handleDownloadPdf = () => {
    if (!isDateRangeValid()) return;
    setDownloading(true);
    printBacCauseListReport(
      validateDateAndFormat(startDate, "yyyy-MM-dd"),
      validateDateAndFormat(endDate, "yyyy-MM-dd"),
      search,
      causeListType
    )
      .then((res) => {
        fileDownload(res.data, "Cause List Report.pdf");
        setDownloading(false);
      })
      .catch((err) => {
        console.error("Cause List PDF error:", err);
        setDownloading(false);
      });
  };

  const dates = data ? Object.keys(data) : [];
  let globalCounter = 0;

  return (
    <Box>
      <Typography
        variant="h1"
        component="h1"
        style={{ fontSize: "1.5rem", fontWeight: 500, padding: "2rem", color: TEXT.strong, textAlign: "left" }}
      >
        Cause List Report
      </Typography>

      <Box style={{ backgroundColor: "white" }}>
        <Box style={{ padding: '1rem 1rem 0 1rem', display: 'flex', justifyContent: 'flex-end' }}>
          {/* <GlobalSearch
            value={search}
            onSearchChange={handleSearchChange}
            placeholder="Search Cause List..."
          /> */}
        </Box>
        {/* Filters */}
        <Box className={classes.filtersContainer}>
          <DateRangePickerACCB
            startDate={startDate}
            setStartDate={handleStartDateChange}
            endDate={endDate}
            setEndDate={handleEndDateChange}
            required
          />
          <TextField
            variant="outlined"
            size="small"
            select
            label="Type"
            value={causeListType}
            onChange={(e) => setCauseListType(e.target.value)}
            SelectProps={{ native: true }}
            style={{ minWidth: 150 }}
          >
            <option value="main">Main Cause List</option>
            <option value="supplementary">Supplementary Cause List</option>
          </TextField>
          <Button
            className={classes.buttonSearch}
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? "Loading…" : "Search"}
          </Button>
          <Button
            className={classes.buttonPrint}
            variant="outlined"
            color="primary"
            size="small"
            endIcon={<PrintOutlined />}
            onClick={handleDownloadPdf}
            disabled={downloading}
          >
            {downloading ? "Downloading…" : "Download PDF"}
          </Button>
        </Box>

        {dateError && (
          <Typography style={{ color: TEXT.error, padding: "1rem 12px", fontSize: "0.9375rem" }}>
            {dateError}
          </Typography>
        )}

        {error && (
          <Typography style={{ color: TEXT.error, padding: "1rem 12px", fontSize: "0.9375rem" }}>
            {error}
          </Typography>
        )}

        {/* Table */}
        <Box style={{ padding: "1.5rem 0" }}>
          {loading ? (
            <Box style={{ padding: "2rem 0" }}>
              <CircularCenteredLoader />
            </Box>
          ) : data === null ? (
            <Typography style={{ padding: "2rem", textAlign: "center", color: TEXT.muted }}>
              Select a date range and click Search.
            </Typography>
          ) : dates.length === 0 ? (
            <Typography style={{ padding: "2rem", textAlign: "center", color: TEXT.muted }}>
              No cases found for this date range.
            </Typography>
          ) : (
            <Box className={classes.tableWrapper}>
              <TableContainer component={Paper} style={{ overflowX: "auto" }}>
                <Table size="small" style={{ minWidth: 1000, border: "1px solid #ddd" }}>
                  <TableHead>
                    <TableRow className={classes.tableHeadRow}>
                      <TableCell style={{ width: 50, whiteSpace: "nowrap" }}>S.No.</TableCell>
                      <TableCell style={{ minWidth: 140, whiteSpace: "nowrap" }}>Case No.</TableCell>
                      <TableCell style={{ minWidth: 200 }}>Case Title</TableCell>
                      <TableCell style={{ minWidth: 260 }}>Counsel(s)</TableCell>
                      <TableCell style={{ minWidth: 160 }}>Arbitrator(s)</TableCell>
                      <TableCell style={{ minWidth: 120 }}>Stage</TableCell>
                      <TableCell style={{ minWidth: 120 }}>Payment</TableCell>
                      <TableCell style={{ minWidth: 110, whiteSpace: "nowrap" }}>Time</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {dates.map((date) => {
                      const rooms = data[date] || {};
                      const roomNames = Object.keys(rooms);
                      return (
                        <React.Fragment key={date}>
                          {/* Date header row — dark purple */}
                          <TableRow>
                            <TableCell colSpan={8} className={classes.dateHeader}>
                              {date}
                            </TableCell>
                          </TableRow>

                          {roomNames.map((room) => {
                            const cases = rooms[room] || [];
                            return (
                              <React.Fragment key={`${date}-${room}`}>
                                {/* Room header row — pale gold */}
                                <TableRow>
                                  <TableCell colSpan={8} className={classes.roomHeader}>
                                    {room}
                                  </TableCell>
                                </TableRow>

                                {cases.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={8} align="center" style={{ padding: "12px 0" }}>
                                      <Typography color="textSecondary">No cases for this room.</Typography>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  cases.map((item, idx) => {
                                    globalCounter += 1;
                                    const slots = Array.isArray(item.slots) ? item.slots : [];
                                    const arbitratorNames = Array.isArray(item.arbitrator_names)
                                      ? item.arbitrator_names
                                      : item.arbitrator_names
                                        ? [item.arbitrator_names]
                                        : [];

                                    return (
                                      <TableRow key={`${item.id}-${idx}`} hover>
                                        <TableCell className={classes.tableBodyCell}>
                                          {globalCounter}
                                        </TableCell>
                                        <TableCell
                                          className={classes.tableBodyCell}
                                          style={{ whiteSpace: "nowrap" }}
                                        >
                                          {item.tiac_custom_dispute_id || "—"}
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                          {item.case_name || "—"}
                                        </TableCell>
                                        <TableCell style={{ padding: "10px", verticalAlign: "top" }}>
                                          <CounselGroup
                                            label="Claimant Counsel(s)"
                                            counsels={item.claimant_counsels}
                                            classes={classes}
                                          />
                                          <CounselGroup
                                            label="Respondent Counsel(s)"
                                            counsels={item.respondent_counsels}
                                            classes={classes}
                                          />
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                          {arbitratorNames.length === 0 ? (
                                            <Typography className={classes.arbitratorLine}>—</Typography>
                                          ) : (
                                            arbitratorNames.map((name, i) => (
                                              <Typography key={i} className={classes.arbitratorLine}>
                                                {name} {((arbitratorNames?.length > 1) && (i === 0)) ? '(Presiding)' : ''}
                                              </Typography>
                                            ))
                                          )}
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                          {item.stage || "—"}
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                          {item.financial_status || "—"}
                                        </TableCell>
                                        <TableCell
                                          className={classes.tableBodyCell}
                                          style={{ whiteSpace: "nowrap" }}
                                        >
                                          {slots.length === 0 ? (
                                            "—"
                                          ) : (
                                            slots.map((slot, slotIndex) => {
                                              const fromTime = slot?.from_time;
                                              const toTime = slot?.to_time;
                                              const timeStr = fromTime && toTime
                                                ? `${fromTime} – ${toTime}`
                                                : fromTime || "—";

                                              return (
                                                <Typography key={slot.slot_id || slotIndex} className={classes.tableBodyCell}>
                                                  {timeStr}
                                                </Typography>
                                              );
                                            })
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })
                                )}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
